/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Mantine
import { DonutChart } from '@mantine/charts';
import { Box, Stack } from '@mantine/core';

// UI local components
import { Button } from '../Button';
import { Select } from '../Select/Select';
import { Text } from '../Text';
import { Title } from '../Title';

// Styles
import classes from './DonutChartContributions.module.css';
import './style.css';

// I18n
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type TChartDataItem = {
	name: string;
	value: number;
	color: string;
};

export type TDonutChartContributionsProps = {
	data?: TChartDataItem[];
	title?: string;
	unit?: string;
	buttonText?: string;
	onViewMore: () => void;
	isLoading: boolean;
	onChangeUnit: (unit: string) => void;
	options: string[];
	onlyTco2?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const DonutChartContributions: React.FC<
	TDonutChartContributionsProps
> = ({
	data = [
		{ name: 'Project 1', value: 40, color: '#617eef' },
		{ name: 'Project 2', value: 30, color: '#f0f0f0' },
		{ name: 'Project 3', value: 10, color: '#f0f0f0' },
	],
	title = 'Répartition des contributions',
	buttonText = 'global.viewMore',
	onViewMore,
	onChangeUnit,
	options,
	onlyTco2,
}) => {
	const { t } = useTranslation();

	// 🔹 Tri direct des données du plus grand au plus petit
	const sortedData = [...data].sort(
		(a, b) => b.value - a.value
	);

	const totalValue = sortedData.reduce(
		(sum, item) => sum + item.value,
		0
	);
	const maxValue = Math.max(
		...sortedData.map((d) => d.value)
	);
	const maxProject = sortedData.find(
		(item) => item.value === maxValue
	);

	const [activeValue, setActiveValue] = useState('0%');
	const [activeLabel, setActiveLabel] = useState(
		`${t('global.project')} ...`
	);
	const [hoveredIndex, setHoveredIndex] = useState<
		number | null
	>(null);
	const [selectedUnit, setSelectedUnit] =
		useState<string>('€');

	// 🔹 Couleurs dynamiques selon hover
	const hoverColoredData = sortedData.map(
		(item, index) => {
			if (hoveredIndex === null) {
				return {
					...item,
					color:
						item.value === maxValue
							? '#617eef'
							: '#f0f0f0',
				};
			}
			return {
				...item,
				color:
					index === hoveredIndex ? '#617eef' : '#f0f0f0',
			};
		}
	);

	// 🔹 Top 3 projets
	const topProjects = sortedData
		.slice(0, 3)
		.map((item) => ({
			...item,
			percentage: (
				(item.value / totalValue) *
				100
			).toFixed(2),
		}));

	useEffect(() => {
		if (maxProject) {
			setActiveValue(
				`${((maxProject.value / totalValue) * 100).toFixed(2)}%`
			);
			setActiveLabel(maxProject.name);
		} else {
			setActiveValue('0%');
			setActiveLabel(`${t('global.project')} ...`);
		}
	}, [data]);

	useEffect(() => {
		if (!selectedUnit) {
			setSelectedUnit('€');
			onChangeUnit('€');
		}
	}, [selectedUnit, onChangeUnit]);

	useEffect(() => {
		if (onlyTco2) {
			setSelectedUnit('tCO2e');
			onChangeUnit('tCO2e');
		}
	}, [onlyTco2]);

	return (
		<Box className={classes.container}>
			<Box className={classes.header}>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{t(title)}
				</Title>

				<Select
					value={selectedUnit}
					size="xs"
					data={options}
					placeholder={t('global.unit')}
					classNames={classes}
					withCheckIcon={false}
					onChange={(value) => {
						if (value) {
							setSelectedUnit(value);
							onChangeUnit(value);
						}
					}}
				/>
			</Box>

			<Box className={classes.body}>
				<div style={{ position: 'relative' }}>
					<DonutChart
						startAngle={180}
						endAngle={0}
						data={hoverColoredData}
						size={350}
						thickness={40}
						withTooltip={false}
						paddingAngle={2}
						classNames={{
							label: classes.labelDonutChart,
							tooltipItemColor: classes.tooltipItemColor,
							tooltipItemName: classes.tooltipItemName,
							tooltipItemData: classes.tooltipItemData,
							tooltipLabel: classes.tooltipLabel,
						}}
						pieProps={{
							onMouseEnter: (_, index) => {
								setHoveredIndex(index);
								setActiveValue(
									`${((sortedData[index].value / totalValue) * 100).toFixed(2)}%`
								);
								setActiveLabel(sortedData[index].name);
							},
							onMouseLeave: () => {
								setHoveredIndex(null);
								if (maxProject) {
									setActiveValue(
										`${((maxProject.value / totalValue) * 100).toFixed(2)}%`
									);
									setActiveLabel(maxProject.name);
								}
							},
						}}
						data-testid="donut-chart-id"
					/>

					<Stack
						style={{
							position: 'absolute',
							top: '38%',
							left: '36%',
							width: '100px',
							maxWidth: '100px',
						}}
						gap="0"
						align="center"
					>
						<Title
							order={5}
							c="var(--mantine-color-igluPrimary-9)"
						>
							{activeValue}
						</Title>
						<Text
							ta="center"
							c="var(--mantine-color-igluSecondary-9)"
							variant="caption"
						>
							{activeLabel}
						</Text>
					</Stack>
				</div>
			</Box>

			<Box className={classes.footer}>
				{topProjects.map((project, index) => (
					<Stack
						key={`${project.name}-${index}`}
						gap="0"
						className={classes.footerInfo}
						pr={'3px'}
					>
						<Text
							c="var(--mantine-color-igluSecondary-9)"
							variant="caption"
						>
							{project.name}
						</Text>
						<Text
							c="var(--mantine-color-igluPrimary-9)"
							variant="subtitle1"
						>
							{project.percentage}%
						</Text>
					</Stack>
				))}
				<Box className={classes.footerButton}>
					<Button onClick={onViewMore} variant="outline">
						{t(buttonText)}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};
